import React, { useState, useEffect, useRef, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Fab,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import StopIcon from '@mui/icons-material/Stop'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import useChatAssistant, { ActiveToolCall, ChatContext } from '../../hooks/useChatAssistant'

const TOOL_LABELS: Record<string, string> = {
  get_backtests: 'Searching backtests',
  get_backtest: 'Loading backtest',
  get_backtest_trades: 'Loading trades',
  get_trade_profits: 'Analyzing profits',
  get_alerts: 'Searching alerts',
  get_alert: 'Loading alert',
  get_user_info: 'Loading user info',
  get_pattern: 'Loading pattern',
  lookup_user: 'Looking up user',
}

interface ChatAssistantProps {
  backtest?: any
}

export default function ChatAssistant({ backtest }: ChatAssistantProps) {
  const theme = useTheme()
  const { usermeta, tradingHouse } = useApplicationSuiteContext()
  const {
    messages,
    sendMessage,
    cancel,
    resetConversation,
    isStreaming,
    activeToolCalls,
    completedToolCalls,
    error,
  } = useChatAssistant(
    tradingHouse as 'gtf' | 'greenchart',
    backtest?.id ? ({ backtestId: backtest.id } as ChatContext) : undefined
  )

  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [showDisclaimer, setShowDisclaimer] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const isNearBottomRef = useRef(true)

  const isAdmin = usermeta?.isAdmin
  const companyName = tradingHouse === 'gtf' ? 'Day Trade U (DTU)' : 'GreenChart'

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current
    if (!el) return
    // Consider "near bottom" if within 80px of the bottom
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }, [])

  const scrollToBottom = useCallback(() => {
    if (isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, activeToolCalls, scrollToBottom])

  if (!isAdmin) {
    return null
  }

  const handleToggle = () => setIsOpen(!isOpen)
  const handleClose = () => setIsOpen(false)

  const handleSend = async (messageText?: string) => {
    const text = messageText || inputValue.trim()
    if (!text || isStreaming) return
    setInputValue('')
    isNearBottomRef.current = true
    await sendMessage(text)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleAnalyzeStrategy = () => {
    if (!backtest) return
    handleSend(
      'Please analyze this backtest and provide key insights, areas for improvement, and any risk warnings.'
    )
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 1,
      }}
    >
      <Collapse in={isOpen}>
        <Paper
          elevation={8}
          sx={{
            width: 420,
            height: 560,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.custom2,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: theme.palette.background.custom4,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <AutoAwesomeIcon sx={{ color: theme.palette.primary.main }} fontSize="small" />
              <Typography variant="subtitle1" fontWeight="bold">
                Assistant
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  backgroundColor: theme.palette.warning.dark,
                  color: theme.palette.warning.contrastText,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  fontSize: '0.6rem',
                  fontWeight: 'bold',
                }}
              >
                ADMIN
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={resetConversation}
                size="small"
                title="New conversation"
                disabled={isStreaming}
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => setShowDisclaimer(!showDisclaimer)}
                size="small"
                sx={{ color: showDisclaimer ? theme.palette.primary.main : 'inherit' }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Disclaimer */}
          {showDisclaimer && (
            <Box
              sx={{
                p: 1.5,
                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, display: 'block', lineHeight: 1.4 }}
              >
                The Assistant is an AI-powered tool provided for educational and informational
                purposes only. It does not provide financial, investment, or trading advice.{' '}
                {companyName} is not responsible or liable for any strategies, analyses,
                recommendations, or results generated by this tool.
              </Typography>
            </Box>
          )}

          {/* Messages Area */}
          <Box
            ref={messagesContainerRef}
            onScroll={handleScroll}
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              backgroundColor: theme.palette.background.custom3,
            }}
          >
            {messages.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                textAlign="center"
                px={2}
              >
                <AutoAwesomeIcon
                  sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2, opacity: 0.7 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Ask about your backtests, alerts, patterns, or account — the assistant will look
                  up the data for you.
                </Typography>
                {backtest && (
                  <Button
                    variant="contained"
                    onClick={handleAnalyzeStrategy}
                    startIcon={<AutoAwesomeIcon />}
                    disabled={isStreaming}
                    sx={{ mt: 2 }}
                  >
                    Analyze This Strategy
                  </Button>
                )}
              </Box>
            ) : (
              <>
                {messages.map((msg, index) => {
                  const isLastMessage = index === messages.length - 1
                  const isActiveAssistant = msg.role === 'assistant' && isLastMessage && isStreaming
                  const hasToolActivity =
                    isActiveAssistant &&
                    (activeToolCalls.length > 0 || completedToolCalls.length > 0)

                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '85%',
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor:
                            msg.role === 'user'
                              ? 'rgba(29, 195, 14, 0.15)'
                              : theme.palette.background.custom4,
                          color: theme.palette.text.primary,
                          border: msg.role === 'user' ? '1px solid rgba(29, 195, 14, 0.3)' : 'none',
                        }}
                      >
                        {msg.role === 'user' ? (
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                          >
                            {msg.content}
                          </Typography>
                        ) : (
                          <Box
                            sx={{
                              fontSize: '0.875rem',
                              lineHeight: 1.5,
                              '& p': { m: 0, mb: 1 },
                              '& p:last-child': { mb: 0 },
                              '& ul, & ol': { m: 0, pl: 2.5, mb: 1 },
                              '& ul:last-child, & ol:last-child': { mb: 0 },
                              '& li': { mb: 0.25 },
                              '& strong': { fontWeight: 600 },
                              '& h1, & h2, & h3, & h4': {
                                fontSize: '0.95rem',
                                fontWeight: 600,
                                mt: 1,
                                mb: 0.5,
                              },
                              '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type': {
                                mt: 0,
                              },
                              '& code': {
                                backgroundColor: 'rgba(0,0,0,0.2)',
                                px: 0.5,
                                borderRadius: 0.5,
                                fontSize: '0.8rem',
                              },
                              '& pre': {
                                backgroundColor: 'rgba(0,0,0,0.2)',
                                p: 1,
                                borderRadius: 1,
                                overflow: 'auto',
                                '& code': { backgroundColor: 'transparent', p: 0 },
                              },
                            }}
                          >
                            {/* Tool activity embedded inside the message bubble */}
                            {hasToolActivity && !msg.content && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  mb:
                                    activeToolCalls.length > 0 || completedToolCalls.length > 0
                                      ? 0.75
                                      : 0,
                                }}
                              >
                                <CircularProgress
                                  size={14}
                                  sx={{ color: theme.palette.text.secondary }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ color: theme.palette.text.secondary }}
                                >
                                  {activeToolCalls.length > 0
                                    ? TOOL_LABELS[
                                        activeToolCalls[activeToolCalls.length - 1].toolName
                                      ] || 'Working'
                                    : 'Thinking'}
                                  ...
                                </Typography>
                              </Box>
                            )}
                            {hasToolActivity && (
                              <ToolActivity
                                completedToolCalls={completedToolCalls}
                                activeToolCalls={msg.content ? activeToolCalls : []}
                                theme={theme}
                                defaultExpanded={!msg.content}
                              />
                            )}

                            {msg.content ? (
                              <ReactMarkdown>{msg.content}</ReactMarkdown>
                            ) : isActiveAssistant ? (
                              !hasToolActivity && <CircularProgress size={16} />
                            ) : (
                              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                (empty response)
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )
                })}

                <div ref={messagesEndRef} />
              </>
            )}
          </Box>

          {/* Error display */}
          {error && (
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            </Box>
          )}

          {/* Input Area */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.custom4,
              display: 'flex',
              gap: 1,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Ask anything..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isStreaming}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.background.custom3,
                },
              }}
            />
            {isStreaming ? (
              <IconButton color="error" onClick={cancel} title="Stop">
                <StopIcon />
              </IconButton>
            ) : (
              <IconButton
                color="primary"
                onClick={() => handleSend()}
                disabled={!inputValue.trim()}
              >
                <SendIcon />
              </IconButton>
            )}
          </Box>
        </Paper>
      </Collapse>

      <Fab color="primary" aria-label="AI assistant" onClick={handleToggle} size="medium">
        <AutoAwesomeIcon />
      </Fab>
    </Box>
  )
}

function ToolActivity({
  completedToolCalls,
  activeToolCalls,
  theme,
  defaultExpanded = false,
}: {
  completedToolCalls: Array<{ toolName: string; toolCallId: string; summary: string }>
  activeToolCalls: ActiveToolCall[]
  theme: any
  defaultExpanded?: boolean
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const showExpanded = expanded || defaultExpanded

  if (completedToolCalls.length === 0 && activeToolCalls.length === 0) return null

  return (
    <Box sx={{ py: 0.25 }}>
      {/* Completed tool calls — click to toggle */}
      {completedToolCalls.length > 0 && (
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
            mb: showExpanded ? 0.25 : 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.7rem',
              userSelect: 'none',
            }}
          >
            {showExpanded ? '▾' : '▸'} {completedToolCalls.length} data{' '}
            {completedToolCalls.length === 1 ? 'lookup' : 'lookups'} completed
          </Typography>
        </Box>
      )}

      {/* Expanded details */}
      {showExpanded &&
        completedToolCalls.map((tc) => (
          <Typography
            key={tc.toolCallId}
            variant="caption"
            sx={{
              display: 'block',
              color: theme.palette.text.secondary,
              fontSize: '0.65rem',
              pl: 1.5,
              py: 0.15,
              lineHeight: 1.3,
            }}
          >
            ✓ {TOOL_LABELS[tc.toolName] || tc.toolName}
            {tc.summary ? ` — ${tc.summary}` : ''}
          </Typography>
        ))}

      {/* Active tool calls — always visible with spinner */}
      {activeToolCalls.map((tc) => (
        <Box
          key={tc.toolCallId}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25, pl: 1.5 }}
        >
          <CircularProgress size={10} sx={{ color: theme.palette.text.secondary }} />
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}
          >
            {TOOL_LABELS[tc.toolName] || tc.toolName}...
          </Typography>
        </Box>
      ))}
    </Box>
  )
}
